import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  InputAdornment,
  Skeleton,
  Alert,
} from '@mui/material';
import Header from '../Header';
import Footer from '../Footer';
import { useParams } from 'react-router-dom';
import { API_URL, BASE_URL } from '../Config';
import config from "../common/Config";
import { Iconify } from "src/components/iconify";
import Swal from 'sweetalert2';
import "../common/commonStyles.css";
import { toast } from 'sonner';

const ConfirmShortQty = () => {
  const { picklistNo, productId, orderType } = useParams();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [newSales, setNewSales] = useState([]);
  const [pickQtyAndReason, setPickQty] = useState([]);
  const [confirmedQuantities, setConfirmedQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const columns = [
    { id: "reference_no", label: "Order Id", width: 120 },
    { id: "customer", label: "Customer Name", width: 150 },
    { id: "code", label: "Code", width: 80 },
    { id: "name", label: "Product", width: 200 },
    { id: "quantity", label: "Order Qty", width: 100, numeric: true },
    { id: "confirmed_qty", label: "Confirmed Qty", width: 130 },
    { id: "order_type", label: "Item Type", width: 100 },
    { id: "reason", label: "Reason", width: 150 },
  ];

  const fetchData = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const token = localStorage.getItem("token");

    setLoading(true);
    const formData = new FormData();
    formData.append('searchByFromdate', '');
    formData.append('searchByTodate', '');
    formData.append('draw', '1');
    formData.append('start', '0');
    formData.append('length', '50');
    formData.append('role', '');
    formData.append('picklist_number', picklistNo);
    formData.append('product_id', productId);
    formData.append('order_type', orderType);

    const apiUrl = `${API_URL}/ws-view-short-list`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setNewSales(Array.isArray(data.data) ? data.data : []);
      setPickQty(Array.isArray(data.product) ? data.product : []);

      const initialQuantities = {};
      if (Array.isArray(data.data)) {
        data.data.forEach((row) => {
          if (row.id) {
            initialQuantities[row.id] = row.confirmed_qty || '';
          }
        });
      }
      setConfirmedQuantities(initialQuantities);
    } catch (error) {
      console.error('Error fetching new sales data:', error);
      setNewSales([]);
      setPickQty([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (picklistNo && productId && orderType) {
      fetchData();
    }
  }, [picklistNo, productId, orderType]);

  const filteredSales = useMemo(() => {
    if (!searchTerm.trim()) {
      return newSales;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return newSales.filter((item) => {
      return (
        (item.reference_no && String(item.reference_no).toLowerCase().includes(searchLower)) ||
        (item.customer && item.customer.toLowerCase().includes(searchLower)) ||
        (item.code && String(item.code).toLowerCase().includes(searchLower)) ||
        (item.name && item.name.toLowerCase().includes(searchLower))
      );
    });
  }, [newSales, searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredSales.slice(startIndex, endIndex);
  }, [filteredSales, page, rowsPerPage]);

  const handleConfirmedQtyChange = (rowId, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    if (value !== '' && (isNaN(numValue) || numValue < 0)) {
      return;
    }
    setConfirmedQuantities((prev) => ({
      ...prev,
      [rowId]: value,
    }));
  };

  const validateQuantities = () => {
    const totalPickedQty = parseFloat(
      pickQtyAndReason.reduce((sum, item) => sum + (parseFloat(item.picked_qty) || 0), 0)
    ) || 0;

    let totalConfirmedQty = 0;
    Object.values(confirmedQuantities).forEach((value) => {
      totalConfirmedQty += parseFloat(value) || 0;
    });

    return totalConfirmedQty === totalPickedQty;
  };

  useEffect(() => {
    const isValid = validateQuantities();
    setIsSubmitDisabled(!isValid);
  }, [confirmedQuantities, pickQtyAndReason]);

  const totalPickedQty = useMemo(() => {
    return pickQtyAndReason.reduce((sum, item) => sum + (parseFloat(item.picked_qty) || 0), 0) || 0;
  }, [pickQtyAndReason]);

  const totalConfirmedQty = useMemo(() => {
    return Object.values(confirmedQuantities).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
  }, [confirmedQuantities]);

  const handleSubmit = async () => {
    if (!validateQuantities()) {
      toast.error(`Total confirmed quantity (${totalConfirmedQty}) must equal total picked quantity (${totalPickedQty}).`);
      return;
    }

    const formData = new FormData();
    newSales.forEach((row) => {
      const confirmedQty = confirmedQuantities[row.id];
      if (confirmedQty && row.product_id && row.id) {
        formData.append('order_id[]', row.id);
        formData.append('product_id[]', row.product_id);
        formData.append('confirm_qty[]', confirmedQty);
        formData.append('picklist_number', picklistNo);
      }
    });

    const apiUrl = `${API_URL}/update_short_quantity`;
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'true') {
        toast.success('Quantities successfully updated!');
        window.location.href = `${BASE_URL}/picking/view_short_qty/${picklistNo}`;
      } else {
        toast.error('Failed to update quantities.');
      }
    } catch (error) {
      console.error('Error submitting confirmed quantities:', error);
      toast.error('An error occurred while updating quantities.');
    }
  };

  const getReason = (rowId) => {
    const item = pickQtyAndReason.find((item) => item.id === rowId);
    return item?.no_pick_reason || 'N/A';
  };

  const getPickedQty = (rowId) => {
    const item = pickQtyAndReason.find((item) => item.id === rowId);
    return parseFloat(item?.picked_qty) || 0;
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2}}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={5}>
                <Typography sx={{ fontWeight: 500, color: "#212b36", fontSize: "1.125rem" }}>
                  Confirm Short Qty - <Box sx={{ fontWeight: 400 }} component="span"> Picklist #{picklistNo}</Box>
                </Typography>
              </Grid>
              <Grid item xs={12} md={3} display="flex" alignItems="center" gap={1}>
                <Grid item>
                  <Typography sx={{ fontSize: "0.875rem", color: "#212b36", fontWeight: 600 }}>
                    Picked QTY :
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography sx={{ fontSize: "0.875rem", color: "#637381" }}>
                    {totalPickedQty}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  className="input-border"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  placeholder="Search orders..."
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table
              size="small"
              className="table-cell-border"
            >
              <colgroup>
                {columns.map((column) => (
                  <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow className="table-cell-header">
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? "right" : "left"}
                      sx={{
      
                        width: column.width ? `${column.width}px` : "auto"
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.numeric ? "right" : "left"}
                          sx={{ py: 1, fontSize: "0.75rem", width: column.width ? `${column.width}px` : "auto" }}
                        >
                          <Skeleton variant="text" width="60%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedSales.length > 0 ? (
                  paginatedSales.map((row, rowIndex) => {
                    const pickedQty = getPickedQty(row.id);
                    return (
                      <TableRow
                        key={row.id || rowIndex}
                        hover
                        className="table-cell-style"
                      >
                        <TableCell >
                          {row.reference_no}
                        </TableCell>
                        <TableCell >
                          {row.customer}
                        </TableCell>
                        <TableCell >
                          {row.code}
                        </TableCell>
                        <TableCell >
                          {row.name}
                        </TableCell>
                        <TableCell align="right" >
                          {parseInt(row.quantity) || 0}
                        </TableCell>
                        <TableCell >
                          <TextField
                            className="input-border"
                            type="number"
                            size="small"
                            value={confirmedQuantities[row.id] || ''}
                            onChange={(e) => handleConfirmedQtyChange(row.id, e.target.value)}
                            inputProps={{
                              min: 0,
                              max: pickedQty,
                              step: 1,
                            }}
                            sx={{
                              width: 100,
                              "& .MuiOutlinedInput-root": {
                                fontSize: "0.75rem",
                                height: 32,
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                          {row.order_type}
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: "0.75rem", color: "#637381" }}>
                          {getReason(row.id)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ py: 8 }}>
                      <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Box>
              <TablePagination
                component="div"
                count={filteredSales.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="pagination-styles"
              />
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              className="button-variant-contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              sx={{
                m:1,
                "&:disabled": {
                  opacity: 0.48
                }
              }}
            >
              Submit
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
};
export default ConfirmShortQty;
